import { ng } from '@uirouter/angularjs/lib/angular';
import * as angular from 'angular';
import * as moment from 'moment';
import { ngStorage } from 'ngstorage';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { AccountService } from '../../../account/account.service';
import { BundleTypeEnum, IBundleItem } from '../../models/models';
import { BundlesService } from '../../services/bundles.service';

class EditBundleDetailsFormComponent {

    public static $inject = [
        '$localStorage',
        'notificationService',
        'accountService',
        'bundlesService',
        '$uibModal',
        '$translate'
    ];

    public bundle: IBundleItem;
    public waiting: boolean;

    public countryID: number;
    public currencyCssClass: string;
    public dateFormat = 'dd/MM/yy';

    public imageSrc: string;
    public form: ng.IFormController;
    public utcToday: Date = moment.utc(new Date()).utc(true).startOf('day').toDate();

    public startDateOptions = {
        formatYear: 'yyyy',
        startingDay: 0,
        minDate: this.utcToday
    };
    public endDateOptions = {
        formatYear: 'yyyy',
        startingDay: 0,
        minDate: this.utcToday
    };

    public openedValidFrom: boolean;
    public openedValidTo: boolean;
    public needGradient = false;
    public bundleTypeExpanded = false;
    public bundleTypes: BundleTypeEnum[] = [BundleTypeEnum.Fixed, BundleTypeEnum.Discount];
    public bundleTypesEnum = BundleTypeEnum;
    public currentBundleType: BundleTypeEnum;

    private originalValidFrom: Date;
    private originalValidTo: Date;
    private maxFileSize = 5;
    private fileExtensions = ['jpeg', 'jpg', 'png', 'bmp', 'gif'];
    private minSize = {
        w: 1032,
        h: 516
    };
    private aspectRatio = (this.minSize.w / this.minSize.h).toFixed(2);
    private savedBundleValue: number;

    constructor(
        private $localStorage: ngStorage.StorageService,
        private notificationService: NotificationService,
        private accountService: AccountService,
        private bundlesService: BundlesService,
        private $uibModal: ng.ui.bootstrap.IModalService,
        private $translate: angular.translate.ITranslateService,
    ) {
    }

    public async $onInit() {
        this.countryID = this.$localStorage.retailerAuthorizationData.countryID;
        this.currencyCssClass = this.getCurrencyCssClass(this.accountService.userData.currencySign);
        this.resetState();
        const price = this.bundle.BundleValue.Price;
        const discount = this.bundle.BundleValue.DiscountPercent;
        if (price) {
            this.currentBundleType = BundleTypeEnum.Fixed;
            this.savedBundleValue = price;
        } else if (discount) {
            this.currentBundleType = BundleTypeEnum.Discount;
            this.savedBundleValue = discount;
        } else {
            this.currentBundleType = BundleTypeEnum.Fixed;
            this.savedBundleValue = price;
        }
    }

    public $onChanges(onChangesObj: angular.IOnChangesObject): void {
        this.resetState();
    }

    public async useTemplate() {
        if (this.waiting) {
            return;
        }
        try {
            const image = await this.$uibModal.open({
                component: 'createTemplateDialog',
                windowClass: 'modal512',
                backdrop: 'static'
            }).result;
            if (image) {
                this.form.$setDirty();
                await this.saveImage(image);
            }
        } catch (e) {
            return;
        }
    }

    public async uploadLogo() {
        if (this.waiting) {
            return;
        }
        try {
            const image = await this.$uibModal.open({
                component: 'jispPayAvatar',
                resolve: {
                    avatarId: () => this.bundle ? this.bundle.LogoImageID : null,
                    title: () => this.$translate.instant('bundle_image'),
                    sizeText: () => this.$translate.instant('store_photo_min_size_hint', {width: 1032, height: 516}),
                    getUrl: () => this.bundle && this.bundle.LogoImageID ? this.bundlesService.getLogoImageUrl(this.bundle.LogoImageID) : null,
                    imageSize: () => this.minSize,
                    maxFileSizeMb: () => this.maxFileSize,
                    aspectRatio: () => this.aspectRatio,
                    fileExtensions: () => this.fileExtensions
                },
                backdrop: 'static',
                windowClass: 'crop-image-dialog'
            }).result;
            if (image) {
                this.needGradient = true;
                this.form.$setDirty();
                await this.saveImage(image);
            }
        } catch (error) {
            return;
        }
    }

    public deleteImage() {
        this.form.$setDirty();
        this.imageSrc = '';
        this.bundle.LogoImageID = null;
        this.needGradient = false;
    }

    public validateRequired(control: ng.INgModelController, value: string) {
        control.$setValidity('required', !!value);
    }

    public openValidFromDatepicker() {
        this.openedValidFrom = !this.waiting;
        this.openedValidTo = false;
    }

    public openValidToDatepicker() {
        this.openedValidFrom = false;
        this.openedValidTo = !this.waiting;
    }

    public validateValidFrom() {
        this.updateMinEndDate();
        const isValid = this.originalValidFrom && moment.utc(this.bundle.ValidFrom).isSame(this.originalValidFrom) ||
            !this.bundle.ValidFrom || moment.utc(this.bundle.ValidFrom).isSameOrAfter(this.utcToday);
        this.form.ValidFrom.$setValidity('valid-from-error', isValid);
        this.validateValidTo();
    }

    public validateValidTo() {
        const isValid = !this.bundle.ValidTo || moment.utc(this.bundle.ValidTo).isSameOrAfter(this.endDateOptions.minDate);
        this.form.ValidTo.$setValidity('valid-to-error', isValid);
    }

    public validatePrice() {
        this.form.Price.$setValidity('required', !!this.bundle.BundleValue.Price || this.bundle.BundleValue.Price === 0);
        this.form.Price.$setValidity('min', this.bundle.BundleValue.Price !== 0);
        this.form.Price.$setValidity('max', !this.bundle.BundleValue.Price || this.bundle.BundleValue.Price <= 9999.99);
    }

    public validateDiscount() {
        this.form.DiscountPercent.$setValidity('required', !!this.bundle.BundleValue.DiscountPercent || this.bundle.BundleValue.DiscountPercent === 0);
        this.form.DiscountPercent.$setValidity('min', this.bundle.BundleValue.DiscountPercent !== 0);
        this.form.DiscountPercent.$setValidity('max', !this.bundle.BundleValue.DiscountPercent || this.bundle.BundleValue.DiscountPercent <= 100);
        this.form.DiscountPercent.$setValidity('integer',  Number.isInteger(this.bundle.BundleValue.DiscountPercent) || Number.isNaN(this.bundle.BundleValue.DiscountPercent));
    }

    public getBundleTypeName(bundleType: BundleTypeEnum): string {
        switch (bundleType) {
            case BundleTypeEnum.Fixed:
                return this.$translate.instant('fixed_price');
            case BundleTypeEnum.Discount:
                return this.$translate.instant('percentage_discount');
        }
    }

    public changeBundleType(bundleType: BundleTypeEnum) {
        this.currentBundleType = bundleType;
        if (bundleType === BundleTypeEnum.Fixed) {
            this.savedBundleValue = this.bundle.BundleValue.DiscountPercent;
            this.bundle.BundleValue.DiscountPercent = null;
        } else {
            this.savedBundleValue = this.bundle.BundleValue.Price;
            this.bundle.BundleValue.Price = null;
        }
    }

    public getCurrentTypeName(): string {
        return this.getBundleTypeName(this.currentBundleType);
    }

    private resetState() {
        this.imageSrc = this.bundle && this.bundle.LogoImageID ? this.bundlesService.getLogoImageUrl(this.bundle.LogoImageID, 428, 214) : null;
        if (this.bundle) {
            this.bundle.ValidFrom = this.bundle.ValidFrom ? moment.utc(new Date(this.bundle.ValidFrom)).startOf('day').toDate() : null;
            this.bundle.ValidTo = this.bundle.ValidTo ? moment.utc(new Date(this.bundle.ValidTo)).startOf('day').toDate() : null;
        }
        this.originalValidFrom = this.bundle ? this.bundle.ValidFrom : null;
        this.originalValidTo = this.bundle ? this.bundle.ValidTo : null;
        this.updateMinEndDate();
    }

    private updateMinEndDate() {
        this.endDateOptions.minDate = this.bundle && this.bundle.ValidFrom && moment.utc(this.bundle.ValidFrom).isSameOrAfter(this.utcToday) ?
            moment.utc(this.bundle.ValidFrom).startOf('day').toDate() as Date : this.utcToday;
    }

    private getCurrencyCssClass(sign: string): string {
        switch (sign) {
            case '£':
                return 'withPound';
            case 'kr':
                return 'withKrona';
            case '$':
                return 'withDollar';
            case '€':
                return 'withEuro';
            case 'د.إ':
                return 'withDirham';
            default:
                return '';
        }
    }

    private async saveImage(imageFileData: string) {
        try {
            this.bundle.LogoImageID = await this.bundlesService.uploadLogoImage(imageFileData);
            this.imageSrc = this.bundlesService.getLogoImageUrl(this.bundle.LogoImageID, 428, 214);
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
    }
}

angular.module('naut').component('editBundleDetailsFormComponent', {
    template: require('./edit-bundle-details-form.component.html'),
    controller: EditBundleDetailsFormComponent,
    controllerAs: 'ctrl',
    bindings: {
        bundle: '<',
        waiting: '<'
    },
    require: {
        form: '^form'
    }
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/bundles/components/edit-bundle-details-form/edit-bundle-details-form.component.ts